import React, {
	createContext,
	useState,
	useContext,
	useEffect,
	useCallback,
	useMemo,
} from "react";
import { userManager } from "./auth-config";
import { clearCodeSnippetCache } from "./hooks/useCodeSnippet";
import { clearCommentAttentionCache } from "./hooks/useCommentAttention";
import { clearMRNotesCache } from "./hooks/useMRNotes";
import { clearReviewerThreadsCache } from "./hooks/useReviewerThreads";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		// Abandoned redirects leave oidc.* entries behind forever, and nothing
		// else prunes them. Skip it mid-callback so we can't race the exchange
		// for the state entry it is about to consume.
		if (window.location.pathname !== "/callback") {
			userManager
				.clearStaleState()
				.catch((error) => console.error("Error clearing stale state:", error));
		}

		userManager.getUser().then((user) => {
			setUser(user);
			setLoading(false);
		});

		const handleUserLoaded = (user) => setUser(user);
		const handleUserUnloaded = () => {
			clearMRNotesCache();
			clearReviewerThreadsCache();
			clearCommentAttentionCache();
			clearCodeSnippetCache();
			setUser(null);
		};

		userManager.events.addUserLoaded(handleUserLoaded);
		userManager.events.addUserUnloaded(handleUserUnloaded);

		return () => {
			userManager.events.removeUserLoaded(handleUserLoaded);
			userManager.events.removeUserUnloaded(handleUserUnloaded);
		};
	}, []);

	// Stable identities: LoginPage keys an effect off `login`, so a fresh
	// reference each render would re-trigger the redirect on every render.
	const login = useCallback(async () => {
		try {
			await userManager.signinRedirect();
		} catch (error) {
			console.error("Login error:", error);
		}
	}, []);

	const logout = useCallback(async () => {
		try {
			await userManager.removeUser();
			clearMRNotesCache();
			clearReviewerThreadsCache();
			clearCommentAttentionCache();
			clearCodeSnippetCache();
			setUser(null);
		} catch (error) {
			console.error("Logout error:", error);
		}
	}, []);

	const value = useMemo(
		() => ({ user, login, logout, loading }),
		[user, login, logout, loading],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
